import React from 'react';
import axios from 'axios';
import { useSearch } from '../context/SearchContext';

const CategoryCard = ({ category, articles }) => (
  <section className="tile bg-white/[0.06] border border-white/10 rounded-2xl p-6 hover:bg-white/[0.09]">
    <div className="flex items-baseline justify-between mb-4">
      <h2 className="text-lg font-semibold tracking-tight text-[#f5f5f7]">{category}</h2>
      <span className="text-xs tabular-nums text-gray-500">{articles.length}</span>
    </div>
    <ul className="space-y-2.5">
      {articles.map((article, index) => (
        <li key={index} className="text-sm leading-snug">
          {article.link ? (
            <a
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-link hover:underline underline-offset-2"
            >
              {article.article}
            </a>
          ) : (
            <span className="text-gray-300">{article.article}</span>
          )}
        </li>
      ))}
    </ul>
  </section>
);

const SkeletonCard = () => (
  <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6 animate-pulse">
    <div className="h-5 w-1/2 bg-white/10 rounded mb-5"></div>
    <div className="space-y-3">
      <div className="h-3.5 w-full bg-white/10 rounded"></div>
      <div className="h-3.5 w-5/6 bg-white/10 rounded"></div>
      <div className="h-3.5 w-4/6 bg-white/10 rounded"></div>
      <div className="h-3.5 w-5/6 bg-white/10 rounded"></div>
    </div>
  </div>
);

const DeveloperRoadmap = () => {
  const [articles, setArticles] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const { searchTerm } = useSearch();

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get('https://kavi-api-kavi-sekhon-276359988851.us-central1.run.app/api/data/');
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredCategoriesAndArticles = () => {
    if (!searchTerm || searchTerm.length < 2) {
      // If no search term or less than 2 characters, show all categories
      const categories = articles.map((article) => article.category);
      const uniqueCategories = [...new Set(categories)];
      return uniqueCategories.map(category => ({
        category,
        articles: articles.filter(article => article.category === category)
      }));
    }

    // Filter articles that match the search term
    const filteredArticles = articles.filter(article =>
      article.article.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Group filtered articles by category
    const categoriesWithMatchingArticles = {};
    filteredArticles.forEach(article => {
      if (!categoriesWithMatchingArticles[article.category]) {
        categoriesWithMatchingArticles[article.category] = [];
      }
      categoriesWithMatchingArticles[article.category].push(article);
    });

    // Convert to array format
    return Object.entries(categoriesWithMatchingArticles).map(([category, articles]) => ({
      category,
      articles
    }));
  };

  const filteredData = getFilteredCategoriesAndArticles();

  return (
    <div className="max-w-6xl mx-auto px-6 pb-24">
      {/* Hero — two-tone Apple display headline */}
      <header className="pt-16 pb-14 md:pt-24 md:pb-20 text-center">
        <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-[#f5f5f7]">
          Kavi Sekhon
        </h1>
        <p className="text-3xl md:text-5xl font-semibold tracking-tight text-gray-500">
          Developer &amp; Architect
        </p>
        <p className="mt-6 text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
          A curated library of articles and resources on data, AI, ML, and infrastructure.
        </p>
      </header>

      {searchTerm && searchTerm.length >= 2 && filteredData.length === 0 && !isLoading && (
        <div className="text-center py-16">
          <p className="text-gray-400 text-lg">No results for &ldquo;{searchTerm}&rdquo;</p>
          <p className="text-gray-600 text-sm mt-2">Try a different search term.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {isLoading
          ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
          : filteredData.map(({ category, articles }) => (
              <CategoryCard key={category} category={category} articles={articles} />
            ))}
      </div>
    </div>
  );
};

const Home = () => {
  return <DeveloperRoadmap />;
};

export default Home;
